import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { AiOutlineClose } from 'react-icons/ai';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../enums/queryKeys';
import PermissionManagerService from '../../../services/FileManager/permissionManagement.service';
import { PermissionsPicker, splitPermissionValues, toPermissionValues } from '../../../shared/components/PermissionsPicker';
import { usePermissionPrincipals } from '../../../hooks/usePermissionPrincipals';
import ConditionalRender from '../../../shared/components/ConditionalRender';
import { PermissionResourceType } from '../../../enums/permissionEnums';
import './LayerPermissionsModal.scss';

interface LayerPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  resourceId: string;
  resourceType?: PermissionResourceType;
  /** Overrides the default updatePermissions save. */
  onSet?: (userValues: string[], groupValues: string[]) => void | Promise<void>;
  onSaved?: () => void;
  /** Show the assignments without allowing a change. */
  readOnly?: boolean;
}

interface ExistingPermissions {
  userPermissions?: unknown;
  groupPermissions?: unknown;
}

const toIds = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map((entry) => (typeof entry === 'object' && entry !== null && 'id' in entry ? (entry as { id: unknown }).id : entry))
        .filter((id): id is string | number => id !== undefined && id !== null)
        .map(String)
    : [];

const LayerPermissionsModal: React.FC<LayerPermissionsModalProps> = ({
  open,
  onClose,
  resourceId,
  resourceType = PermissionResourceType.LAYER,
  onSet,
  onSaved,
  readOnly = false,
}) => {
  const queryClient = useQueryClient();
  const { getPermissions, updatePermissions } = PermissionManagerService();
  const { users, groups, isLoading } = usePermissionPrincipals(open);
  const { data: existing } = useQuery({ ...getPermissions(resourceId), enabled: open });
  const { mutateAsync: updatePermissionsAsync } = useMutation(updatePermissions());

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const hasSeededRef = useRef(false);

  // Clear on close so an abandoned edit can't leak into the next layer.
  useEffect(() => {
    if (open) return;
    hasSeededRef.current = false;
    setSelectedValues([]);
  }, [open]);

  // Seed once the fetch settles, and only once — a refetch resolving mid-edit
  // would otherwise discard whatever the user has ticked.
  useEffect(() => {
    if (!open || hasSeededRef.current || existing === undefined) return;
    hasSeededRef.current = true;
    const permissions = existing as ExistingPermissions;
    setSelectedValues(toPermissionValues(toIds(permissions.userPermissions), toIds(permissions.groupPermissions)));
  }, [open, existing]);

  const handleSet = async () => {
    if (readOnly) return;
    const { userValues, groupValues } = splitPermissionValues(selectedValues);
    setIsSaving(true);
    try {
      if (onSet) {
        await onSet(userValues, groupValues);
      } else {
        await updatePermissionsAsync({ userValues, groupValues, resourceId, resourceType });
        // The global 30s staleTime would otherwise re-seed the next open from
        // the pre-save response.
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PERMISSIONS, resourceId] });
      }
      onSaved?.();
      onClose();
    } catch {
      // updatePermissions already toasts via its onError; keep the modal open
      // with the selection intact so the user can retry.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} centered closable={false} width={520} className="layer-permissions-modal">
      <div className="layer-permissions-modal__header">
        <span className="layer-permissions-modal__title">Permissions</span>
        <button type="button" onClick={onClose} className="layer-permissions-modal__close">
          <AiOutlineClose />
        </button>
      </div>

      <div className="layer-permissions-modal__picker">
        <PermissionsPicker
          users={users}
          groups={groups}
          selectedValues={selectedValues}
          onChange={setSelectedValues}
          isLoading={isLoading}
          readOnly={readOnly}
        />
      </div>

      <ConditionalRender visible={!readOnly}>
        <div className="layer-permissions-modal__actions">
          <Button className="layer-permissions-modal__set-btn" onClick={handleSet} loading={isSaving} disabled={isSaving}>
            Set
          </Button>
        </div>
      </ConditionalRender>
    </Modal>
  );
};

export default LayerPermissionsModal;
